"use client";
import SectionWrapper from "@cc/section-wrapper";
import {
  alpha,
  Divider,
  Stack,
  Typography,
  TypographyProps,
} from "@mui/material";
import { MotionStack, MotionTypography } from "@cc/motion-components";
import { footerChildrenMotionVariants } from "@/layout/footer/social-links";
import {
  FileDownloadOutlined,
  LanguageRounded,
  MailRounded,
  PhoneRounded,
  SvgIconComponent,
} from "@mui/icons-material";
import { MaterialImage } from "@cc/material-components";
import { StaticImageData } from "next/image";
import BorderedIconButton from "@/commonComponents/bordered-icon-button";
import { FacebookIcon, LinkedInIcon, TwitterIcon } from "@cc/social-icons";
import MLink from "@/commonComponents/m-link";
import { useMemo } from "react";

export type PressReleaseSocialLinksProps = {
  logo?: StaticImageData | string;
  email?: string;
  number?: string;
  website?: string;
  downloadUrl?: string;
  icons?: string[];
};

const PressReleaseSocialLinks = (props: PressReleaseSocialLinksProps) => {
  const { email, logo, number, website, icons, downloadUrl } = props;

  let iconArray = useMemo(() => {
    if (window && icons) {
      const currentUrl = encodeURIComponent(window.location.href);
      return [
        icons.includes("facebook")
          ? {
              Icon: FacebookIcon,
              url: `https://www.facebook.com/sharer/sharer.php?u=${currentUrl}`,
            }
          : {},
        icons.includes("twitter")
          ? {
              Icon: TwitterIcon,
              url: `https://twitter.com/intent/tweet?url=${currentUrl}`,
            }
          : {},
        icons.includes("linkedIn")
          ? {
              Icon: LinkedInIcon,
              url: `https://www.linkedin.com/sharing/share-offsite/?url=${currentUrl}`,
            }
          : {},
      ].filter((item) => !!item);
    }
    return [];
  }, [icons]);

  const contactInfo: {
    icon: SvgIconComponent;
    text?: string;
  }[] = [
    {
      icon: MailRounded,
      text: email,
    },
    {
      icon: PhoneRounded,
      text: number,
    },
    {
      icon: LanguageRounded,
      text: website,
    },
  ].filter((item) => item.text);

  return (
    <SectionWrapper
      SectionProps={{
        bgcolor: "primary.main",
      }}
      divider={
        <Divider
          sx={{
            borderColor: (theme) => alpha(theme.palette.common.white, 0.3),
          }}
        />
      }
      rowGap={{ xs: 3, md: 5 }}
    >
      {(logo || contactInfo.length) && (
        <Stack
          direction={"row"}
          alignItems={"center"}
          width={1}
          justifyContent={"space-between"}
        >
          {logo && (
            <MaterialImage
              src={logo}
              alt={"mukand"}
              width={200}
              height={110}
              sx={{
                width: { xs: 135, md: 200 },
                height: { xs: 72, md: 110 },
              }}
            />
          )}
          {contactInfo.length && (
            <MotionStack
              direction={{
                xs: "column",
                lg: "row",
              }}
              columnGap={3}
              rowGap={1}
              justifyContent={"space-between"}
              width={{ sm: "fit-content", lg: 0.66 }}
              minWidth={"fit-content"}
              variants={footerChildrenMotionVariants}
              divider={
                <Divider
                  flexItem
                  orientation={"vertical"}
                  sx={{
                    borderColor: (theme) =>
                      alpha(theme.palette.common.white, 0.3),
                  }}
                />
              }
            >
              {contactInfo.map((item, index) => {
                const { icon: Icon, text } = item;
                return (
                  <MotionTypography
                    key={index}
                    display={"flex"}
                    alignItems={"center"}
                    variant={"body1"}
                    columnGap={1}
                    whiteSpace={"nowrap"}
                    color={"secondary.500"}
                  >
                    <Icon
                      sx={{
                        fontSize: { xs: 16, md: 20 },
                        color: "common.white",
                      }}
                    />
                    {text}
                  </MotionTypography>
                );
              })}
            </MotionStack>
          )}
        </Stack>
      )}
      {(iconArray.length || downloadUrl) && (
        <Stack
          direction={{ sm: "row" }}
          justifyContent={"space-between"}
          alignItems={"center"}
          rowGap={4}
        >
          {iconArray.length && (
            <Stack direction={"row"} spacing={3} width={"fit-content"}>
              {iconArray.map((item, index) => {
                const { Icon, url } = item;
                if (!Icon || !url) return null;
                return (
                  <Icon
                    key={index}
                    component={MLink}
                    disableLocale
                    href={url}
                    target="_blank"
                    variant={"outlined"}
                    color={"secondary"}
                    SvgIconProps={{
                      width: { xs: 32, md: 37, xxl: 40 },
                      height: { xs: 32, md: 37, xxl: 40 },
                    }}
                  />
                );
              })}
            </Stack>
          )}
          {downloadUrl && (
            <MLink
              href={downloadUrl}
              disableLocale
              target="_blank"
              variant="text"
              color="secondary"
              disableFocusRipple
              disableRipple
              disableTouchRipple
              disableElevation
            >
              <Typography {...actionTypographyDefaultProps}>
                <BorderedIconButton size={"small"} color={"secondary"}>
                  <FileDownloadOutlined
                    fontSize={"small"}
                    color={"secondary"}
                  />
                </BorderedIconButton>
                Download
              </Typography>
            </MLink>
          )}
        </Stack>
      )}
    </SectionWrapper>
  );
};

export default PressReleaseSocialLinks;

const actionTypographyDefaultProps: Omit<TypographyProps, "children"> = {
  variant: "subtitle2",
  sx: {
    display: "flex",
    columnGap: 1,
    width: "fit-content",
    textTransform: "Capitalize",
    alignItems: "center",
  },
};
